/**
 * Dynamic text rotator: ES6 web component.
 * Cycles through bold words bottom-to-top (next word enters from bottom, infinite).
 * Reads data-word-duration and data-time-between from the host; updates .dynamic-text-rotator__rotator-inner transform.
 */
const INNER_SELECTOR = '.dynamic-text-rotator__rotator-inner';
const WORD_SELECTOR = '.dynamic-text-rotator__word';
const LINE_HEIGHT_EM = 1.25;

class DynamicTextRotator extends HTMLElement {
  static get observedAttributes() {
    return ['data-word-duration', 'data-time-between'];
  }

  constructor() {
    super();
    this._intervalId = null;
    this._index = 0;
  }

  connectedCallback() {
    this._start();
  }

  disconnectedCallback() {
    this._stop();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this._stop();
    this._start();
  }

  _start() {
    const inner = this.querySelector(INNER_SELECTOR);
    const words = this.querySelectorAll(WORD_SELECTOR);
    if (!inner || words.length === 0) return;

    const duration = parseInt(this.dataset.wordDuration || '2000', 10);
    const gap = parseInt(this.dataset.timeBetween || '400', 10);
    const totalDelay = duration + gap;

    const n = words.length;
    const cloneWords = () => {
      for (let i = 0; i < n; i++) {
        const clone = words[i].cloneNode(true);
        inner.appendChild(clone);
      }
    };
    cloneWords();
    const allWords = this.querySelectorAll(WORD_SELECTOR);
    const total = allWords.length;

    const setIndex = (i, useTransition = true) => {
      this._index = ((i % total) + total) % total;
      inner.style.transition = useTransition ? '' : 'none';
      const offset = this._index * LINE_HEIGHT_EM;
      inner.style.transform = `translateY(-${offset}em)`;
    };

    setIndex(0, false);
    this._intervalId = setInterval(() => {
      const next = this._index + 1;
      if (next === total) {
        inner.style.transition = 'none';
        setIndex(0, false);
        requestAnimationFrame(() => {
          inner.style.transition = '';
        });
      } else {
        setIndex(next, true);
      }
    }, totalDelay);
  }

  _stop() {
    if (this._intervalId !== null) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }
}

if (!customElements.get('dynamic-text-rotator')) {
  customElements.define('dynamic-text-rotator', DynamicTextRotator);
}
